import {
  ColumnFiltersState,
  PaginationState,
  SortingState,
  Updater,
} from "@tanstack/react-table";
import { useCallback, useRef, useState } from "react";
import type { CustomFieldFilter } from "./customFieldFilter";

export type TableSettings = {
  filters: ColumnFiltersState;
  sorting: SortingState;
  pageSize: number;
  /**
   * The custom field filter (spec `doc-custom-field-filtering-01a11cc6e668eb59`,
   * R32). Settings saved before it read as no custom field filter. Pass it
   * through `pruneCustomFieldFilter` before use (R33).
   */
  customFieldFilter?: CustomFieldFilter;
};

export const DEFAULT_TABLE_SETTINGS: TableSettings = {
  filters: [],
  sorting: [],
  pageSize: 10,
};

export function getTableSettings(tableName: string): TableSettings {
  const tableSettingsStr = localStorage.getItem(`table.${tableName}`);
  const tableSettings =
    tableSettingsStr === null
      ? DEFAULT_TABLE_SETTINGS
      : JSON.parse(tableSettingsStr);
  return tableSettings;
}

export function setTableSettings(
  tableName: string,
  tableSettings: TableSettings,
) {
  const tableSettingsExisting = getTableSettings(tableName);
  localStorage.setItem(
    `table.${tableName}`,
    JSON.stringify({ ...tableSettingsExisting, ...tableSettings }),
  );
}

/**
 * A new settings object, or an updater that derives one from the latest saved
 * settings. Use the updater when one handler can make more than one write (the
 * mobile "Clear" clears the column filters and the custom field filter), so a
 * later write does not restore what an earlier one cleared.
 */
export type TableSettingsUpdate =
  TableSettings | ((prev: TableSettings) => TableSettings);

export type SetTableSettings = (update: TableSettingsUpdate) => void;

export function useTableSettings(
  tableName: string,
  defaults?: Partial<TableSettings>,
) {
  const [tableSettings, setTableSettingsState] = useState({
    ...defaults,
    ...getTableSettings(tableName),
  });

  const latest = useRef<TableSettings>(tableSettings);

  const setTableSettingsCallback = useCallback<SetTableSettings>(
    (update) => {
      const next =
        typeof update === "function" ? update(latest.current) : update;
      latest.current = next;
      setTableSettings(tableName, next);
      setTableSettingsState(next);
    },
    [tableName],
  );

  return [tableSettings, setTableSettingsCallback] as const;
}

export function onColumnFiltersChange(
  filters: ColumnFiltersState,
  setFilters: (value: ColumnFiltersState) => void,
  tableSettings: TableSettings,
  setTableSettings: SetTableSettings,
) {
  return (filtersUpdater: Updater<ColumnFiltersState>) => {
    const newFilters =
      typeof filtersUpdater === "function"
        ? filtersUpdater(filters)
        : filtersUpdater;

    setFilters(newFilters);
    setTableSettings((prev) => ({ ...prev, filters: newFilters }));
  };
}

export function onSortingChange(
  sorting: SortingState,
  setSorting: (value: SortingState) => void,
  tableSettings: TableSettings,
  setTableSettings: SetTableSettings,
) {
  return (sortingUpdater: Updater<SortingState>) => {
    const newSorting =
      typeof sortingUpdater === "function"
        ? sortingUpdater(sorting)
        : sortingUpdater;

    setSorting(newSorting);
    setTableSettings((prev) => ({ ...prev, sorting: newSorting }));
  };
}

export function onPaginationChange(
  pagination: PaginationState,
  setPagination: (value: PaginationState) => void,
  tableSettings: TableSettings,
  setTableSettings: SetTableSettings,
) {
  return (paginationUpdater: Updater<PaginationState>) => {
    const newPagination =
      typeof paginationUpdater === "function"
        ? paginationUpdater(pagination)
        : paginationUpdater;

    setPagination(newPagination);
    setTableSettings((prev) => ({ ...prev, pageSize: newPagination.pageSize }));
  };
}
